import React, { useState, useEffect } from "react";
import { apiCall } from "../Apis/apiCall";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Breadcrumb from "../components/Breadcrumb";
import CustomSelect from "../components/common/CustomSelect";
import ParkingSlotGrid from "../components/child/ParkingSlotGrid";
import ErrorAlert from "../components/ui/ErrorAlert";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import { usePermissions } from "../context/PermissionContext";



const SlotOverviewPage = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [slotToEdit, setSlotToEdit] = useState(null);
  const [formData, setFormData] = useState({ slot_count: "" });
  const [editFormData, setEditFormData] = useState({
    slot_id: "",
    location_id: "",
    slotNumber: "",
    slot_type: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [planLimits, setPlanLimits] = useState({
    maxSlots: 0,
    slotsCreated: 0,
    slotsLeft: 0,
    planName: "",
  });
  const { hasPermission } = usePermissions();
  const [fromHierarchy, setFromHierarchy] = useState(false);
const progressBar = useLocation();
  const navigatePage = progressBar.state?.progressBarNavigate || false;
  const slotTypes = [
    { value: "open", label: "Open" },
    { value: "reserved", label: "Reserved" },
    { value: "occupied", label: "Occupied" },
  ];

  // Fetch branches and plan limits on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setBranchLoading(true);
      try {
        const branchesResponse = await apiCall("get", "/get-client-branches", {
          want_pagination: 0,
        });
        if (branchesResponse?.data?.success) {
          setBranches(branchesResponse.data.data);
        }

        const limitsResponse = await apiCall("get", "/get-dashboard-counts");
        if (limitsResponse?.data?.success) {
          setPlanLimits({
            maxSlots: limitsResponse.data.data.max_vehicles_allowed,
            slotsCreated: limitsResponse.data.data.slots_created,
            slotsLeft: limitsResponse.data.data.slots_left,
            planName: limitsResponse.data.data.plan_name,
          });
        }
        handleApiResponse(
          branchesResponse.data,
          "Failed to fetch branch",
          "GET"
        );
      } catch (err) {
        const error = err.response?.data || err.message;
        setError("Failed to fetch initial data");
        handleApiResponse(error, "Failed to fetch branch", "GET");
      }
      setBranchLoading(false);
    };
    fetchInitialData();
  }, []);

  // Handle auto-selection from state (from hierarchy page)
  useEffect(() => {
    const state = routerLocation.state;
    if (state?.fromHierarchy) {
      setFromHierarchy(true);
    }
    if (state?.branchId) {
      setSelectedBranch(state.branchId);
    }
    if (state?.locationId && selectedBranch) {
      setSelectedLocation(state.locationId);
    }
  }, [routerLocation.state, branches, locations]);

  // Fetch locations when a branch is selected
  useEffect(() => {
    if (!selectedBranch) return;
    const fetchLocations = async () => {
      setLocationLoading(true);
      try {
        const response = await apiCall(
          "get",
          `/get-branch-locations/${selectedBranch}`,
          { want_pagination: 0 }
        );
        if (response?.data?.success) {
          setLocations(response.data.data);
          setSelectedLocation("");
          setSlots([]);
        }
        handleApiResponse(response.data, "Failed to fetch locations", "GET");
      } catch (err) {
        const error = err.response?.data || err.message;
        setError("Failed to fetch locations");
        handleApiResponse(error, "Failed to fetch locations", "GET");
      }
      setLocationLoading(false);
    };
    fetchLocations();
  }, [selectedBranch]);

  // Fetch slots when a location is selected
  useEffect(() => {
    if (!selectedLocation) return;
    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        const response = await apiCall(
          "get",
          `/get-slots-by-location-id/${selectedLocation}`
        );
        if (response?.data?.success) {
          setSlots(response.data.data);
        }
        handleApiResponse(response.data, "fetch slots", "GET");
      } catch (err) {
        setError("Failed to fetch slots");
        const error = err.response?.data || err.message;
        handleApiResponse(error, "Failed to fetch slots", "GET");
      }
      setSlotsLoading(false);
    };
    fetchSlots();
  }, [selectedLocation]);

  // Handle delete action
  const handleDeleteClick = (slotId, slotNumber) => {
    setSlotToDelete({ id: slotId, label: slotNumber });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (slotToDelete) {
      setLoading(true);
      try {
        const response = await apiCall(
          "delete",
          `/delete-parking-slot/${slotToDelete.id}`
        );
        handleApiResponse(
          response.data,
          `Slot "${slotToDelete.label}" deleted successfully`,
          "DELETE"
        );
        setSlots((prev) =>
          prev.filter((slot) => slot.slot_id !== slotToDelete.id)
        );

        const limitsResponse = await apiCall("get", "/get-dashboard-counts");
        if (limitsResponse?.data?.success) {
          setPlanLimits({
            maxSlots: limitsResponse.data.data.max_vehicles_allowed,
            slotsCreated: limitsResponse.data.data.slots_created,
            slotsLeft: limitsResponse.data.data.slots_left,
            planName: limitsResponse.data.data.plan_name,
          });
        }

        setShowDeleteModal(false);
        setSlotToDelete(null);
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message || "Failed to delete slot";
        const error = err.response?.data || err.message;
        handleApiResponse(error, "Failed to delete slot", "DELETE");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSlotToDelete(null);
  };

  // Handle edit action
  const handleEditClick = (slot) => {
    setSlotToEdit(slot);
    setEditFormData({
      slot_id: slot.slot_id,
      location_id: slot.location_id?.toString() || "",
      slotNumber: slot.slot_number || "",
      slot_type: slot.slot_type || "open",
    });
    setShowEditModal(true);
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setSlotToEdit(null);
    setEditFormErrors({});
  };

  const handleEditInputChange = (selected, name) => {
    setEditFormData((prev) => ({
      ...prev,
      [name]: selected ? selected.value : "",
    }));
    if (editFormErrors[name]) {
      setEditFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleEditTextInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (editFormErrors[name]) {
      setEditFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!editFormData.location_id) {
      newErrors.location_id = "Location is required";
    }
    if (!editFormData.slotNumber.trim()) {
      newErrors.slotNumber = "Slot number is required";
    }
    if (!editFormData.slot_type) {
      newErrors.slot_type = "Slot type is required";
    }
    setEditFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (validateEditForm()) {
      setLoading(true);
      try {
        const res = await apiCall(
          "put",
          `/update-parking-slot/${editFormData.slot_id}`,
          {
            location_id: parseInt(editFormData.location_id),
            slot_number: editFormData.slotNumber,
            slot_type: editFormData.slot_type,
          }
        );
        handleApiResponse(
          res.data,
          `Slot "${editFormData.slotNumber}" updated successfully`,
          "PUT"
        );

        setSlots((prev) =>
          prev.map((slot) =>
            slot.slot_id === editFormData.slot_id
              ? {
                  ...slot,
                  location_id: parseInt(editFormData.location_id),
                  slot_number: editFormData.slotNumber,
                  slot_type: editFormData.slot_type,
                }
              : slot
          )
        );

        setShowEditModal(false);
        setSlotToEdit(null);
        setEditFormErrors({});
      } catch (err) {
        const error = err.response?.data || err.message;
        handleApiResponse(error, "Failed to update slot", "PUT");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle create slot modal
  const handleCreateClick = () => {
    if (!selectedBranch || !selectedLocation) {
      handleCustomResponse(
        "error",
        "Please select both branch and location first"
      );
      return;
    }
    if (planLimits.slotsLeft <= 0) {
      handleCustomResponse(
        "error",
        "You have reached your slot limit. Please upgrade your plan.!"
      );
      return;
    }
    setShowCreateModal(true);
  };

  const handleCreateCancel = () => {
    setShowCreateModal(false);
    setFormData({ slot_count: "" });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.slot_count.trim()) {
      newErrors.slot_count = "Slot count is required";
    } else if (parseInt(formData.slot_count) <= 0) {
      newErrors.slot_count = "Slot count must be greater than 0";
    } else if (!Number.isInteger(parseFloat(formData.slot_count))) {
      newErrors.slot_count = "Slot count must be a whole number";
    } else if (parseInt(formData.slot_count) > planLimits.slotsLeft) {
      newErrors.slot_count = `You can only create ${planLimits.slotsLeft} more slots with your current plan`;
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const selectedLocationData = locations.find(
          (loc) => loc.location_id === parseInt(selectedLocation)
        );
        if (!selectedLocationData) {
          handleCustomResponse("error", "Selected location not found!");
          return;
        }

        const response = await apiCall("post", "/add-parking-slot", {
          branch_id: selectedBranch,
          location_id: selectedLocation,
          slot_count: parseInt(formData.slot_count),
        });

        if (response?.data?.success) {
          handleApiResponse(
            response.data,
            `${formData.slot_count} slot(s) created successfully`,
            "POST"
          );

          setShowCreateModal(false);
          setFormData({ slot_count: "" });

          const slotsResponse = await apiCall(
            "get",
            `/get-slots-by-location-id/${selectedLocation}`
          );
          if (slotsResponse?.data?.success) {
            setSlots(slotsResponse.data.data);
          }

          const limitsResponse = await apiCall("get", "/get-dashboard-counts");
          if (limitsResponse?.data?.success) {
            setPlanLimits({
              maxSlots: limitsResponse.data.data.max_vehicles_allowed,
              slotsCreated: limitsResponse.data.data.slots_created,
              slotsLeft: limitsResponse.data.data.slots_left,
              planName: limitsResponse.data.data.plan_name,
            });
          }

          if (fromHierarchy) {
            navigate("/branches-hierarchy");
          }
        }
        if(navigatePage){

          navigate(-1);
        }
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message || "Failed to create slots";
        const error = err.response?.data || err.message;
        handleApiResponse(error, "Failed to create slots", "POST");

        if (errorMessage.includes("limit") || errorMessage.includes("plan")) {
          const limitsResponse = await apiCall("get", "/get-dashboard-counts");
          if (limitsResponse?.data?.success) {
            setPlanLimits({
              maxSlots: limitsResponse.data.data.max_vehicles_allowed,
              slotsCreated: limitsResponse.data.data.slots_created,
              slotsLeft: limitsResponse.data.data.slots_left,
              planName: limitsResponse.data.data.plan_name,
            });
          }
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const getSlotCounts = () => {
    const counts = { open: 0, reserved: 0, occupied: 0 };
    slots.forEach((slot) => {
      if (counts.hasOwnProperty(slot.slot_type)) {
        counts[slot.slot_type]++;
      }
    });
    return counts;
  };

  const slotCounts = getSlotCounts();

  const branchOptions = branches.map((branch) => ({
    value: branch.branch_id,
    label: `${branch.branch_name} (${branch.branch_code})`,
  }));

  const locationOptions = locations.map((location) => ({
    value: location.location_id,
    label: `${location.location_name} (${location.location_code})`,
  }));

  const load = () => {
    window.location.reload();
  };

  if (error) {
    return <ErrorAlert error={error} onRetry={load} />;
  }

  const { maxSlots, slotsCreated } = planLimits;
  const percentage = (slotsCreated / maxSlots) * 100;

  return (
    <>
      <Breadcrumb title="Parking Slots Overview" />
      <div className="container-fluid">
        <div className="row">
          <div className="slot-overview-card">
            <div className="slot-overview-filter-section">
              <div className="slot-overview-select-wrapper">
                <label className="slot-overview-label">Select Branch</label>
                <CustomSelect
                  name="branch"
                  value={selectedBranch}
                  onChange={(selectedOption) =>
                    setSelectedBranch(
                      selectedOption ? selectedOption.value : ""
                    )
                  }
                  options={branchOptions}
                  placeholder="Choose a branch"
                  isLoading={branchLoading}
                  loadingMessage={() => "Loading branches..."}
                  noOptionsMessage={() => (
                    <div>
                      <p>No branches available</p>
                      <Link to="/branch-create" className="text-primary-600">
                        + Create New Branch
                      </Link>
                    </div>
                  )}
                  isClearable
                  addNewLabel="Add New Branch"
                  addNewRoute="/branch-create"
                />
              </div>

              <div className="slot-overview-select-wrapper">
                <label className="slot-overview-label">Select Location</label>
                <CustomSelect
                  name="location"
                  value={selectedLocation}
                  onChange={(selectedOption) =>
                    setSelectedLocation(
                      selectedOption ? selectedOption.value : ""
                    )
                  }
                  options={locationOptions}
                  placeholder="Choose a location"
                  isLoading={locationLoading}
                  loadingMessage={() => "Loading locations..."}
                  noOptionsMessage={() => (
                    <div>
                      <p>No locations available</p>
                      <Link
                        to="/branch-location-create"
                        className="text-primary-600"
                      >
                        + Create New Location
                      </Link>
                    </div>
                  )}
                  isDisabled={!selectedBranch}
                  isClearable
                  addNewLabel="Add New Location"
                  addNewRoute="/branch-location-create"
                />
              </div>
            </div>
          </div>

          {slotsLoading  ? (
            <div>
              <div className="slot-overview-card">
                <SlotOverviewShimmer />
              </div>
            </div>
          ) : slots.length > 0 ? (
            <div>
              <div className="slot-overview-card">
                <div className="slot-overview-stats-grid">
                  <div className="slot-overview-stat-card">
                    <div className="slot-overview-stat-icon slot-overview-stat-icon-available">
                      <Icon icon="mdi:car-outline" />
                    </div>
                    <div className="slot-overview-stat-content">
                      <div className="slot-overview-stat-number slot-overview-stat-number-available">
                        {slotCounts.open}
                      </div>
                      <div className="slot-overview-stat-label">
                        Available Slots
                      </div>
                    </div>
                  </div>
                  <div className="slot-overview-stat-card">
                    <div className="slot-overview-stat-icon slot-overview-stat-icon-reserved">
                      <Icon icon="mdi:car-clock" />
                    </div>
                    <div className="slot-overview-stat-content">
                      <div className="slot-overview-stat-number slot-overview-stat-number-reserved">
                        {slotCounts.reserved}
                      </div>
                      <div className="slot-overview-stat-label">
                        Reserved Slots
                      </div>
                    </div>
                  </div>
                  <div className="slot-overview-stat-card">
                    <div className="slot-overview-stat-icon slot-overview-stat-icon-occupied">
                      <Icon icon="mdi:car" />
                    </div>
                    <div className="slot-overview-stat-content">
                      <div className="slot-overview-stat-number slot-overview-stat-number-occupied">
                        {slotCounts.occupied}
                      </div>
                      <div className="slot-overview-stat-label">
                        Occupied Slots
                      </div>
                    </div>
                  </div>
                  <div className="slot-overview-stat-card">
                    <div className="slot-overview-stat-icon slot-overview-stat-icon-total">
                      <Icon icon="mdi:parking" />
                    </div>
                    <div className="slot-overview-stat-content">
                      <div className="slot-overview-stat-number slot-overview-stat-number-total">
                        {slots.length}
                      </div>
                      <div className="slot-overview-stat-label">
                        Total Slots
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="row justify-content-center mb-4">
                    <div className="col-12 text-center">
                      <div className="rounded p-5 border">
                        <h6 className="fw-bold mb-3">
                          Current Plan{" "}
                          <span className="badge bg-success-100 text-success rounded-pill">
                            {planLimits.planName}
                          </span>
                        </h6>
                        <p className="text-secondary-light mb-4">
                          Manage your slot allocation and create new slots as
                          needed
                        </p>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-neutral-400">
                            Usage Progress
                          </span>
                          <span className="">
                            {planLimits.slotsCreated} / {planLimits.maxSlots}
                          </span>
                        </div>
                        <div
                          className="progress rounded-pill"
                          style={{ height: "8px" }}
                        >
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-end small text-neutral-500 mt-1">
                          {planLimits.maxSlots} slots
                        </div>
                        <div>
                          {hasPermission("add_slot") && (
                            <button
                              onClick={handleCreateClick}
                              className="slot-overview-create-button btn btn-primary-600"
                              disabled={planLimits.slotsLeft <= 0}
                            >
                              <Icon icon="mdi:plus" />
                              Add New Slot
                            </button>
                          )}
                        </div>
                        {planLimits.slotsLeft <= 0 && (
                          <div className="row justify-content-center">
                            <div className="col-12">
                              <div className="alert alert-danger bg-danger-surface text-danger-main rounded-12 text-center">
                                You've reached your slot limit.{" "}
                                <Link to={"/subscription"}>Upgrade</Link> your
                                plan to create more slots.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="slot-overview-card">
                <ParkingSlotGrid
                  slots={slots}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                />
              </div>
            </div>
          ) : (
            <div className="slot-overview-empty-state">
              <div className="slot-overview-empty-state-icon">
                <Icon icon="mdi:parking" />
              </div>
              <p className="slot-overview-empty-state-description">
                {!selectedBranch
                  ? "Please select a branch to view parking slots"
                  : !selectedLocation
                  ? "Please select a location to view parking slots"
                  : "No slots have been created for this location yet"}
              </p>
              {selectedBranch && selectedLocation && (
                <>
                  {planLimits.slotsLeft <= 0 ? (
                    <div className="row justify-content-center">
                      <div className="col-12 col-md-5">
                        <div className="alert alert-danger bg-danger-surface text-danger-main rounded-12 text-center">
                          You've reached your slot limit.{" "}
                          <Link to={"/subscription"}>Upgrade</Link> your plan to
                          create more slots.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {hasPermission("add_slot") && (
                        <button
                          onClick={handleCreateClick}
                          className="slot-overview-create-button btn btn-primary-600"
                        >
                          <Icon icon="mdi:plus" />
                          Create Your First Slot
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Delete Confirmation Modal */}
          <div
            className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
            tabIndex="-1"
            role="dialog"
            style={{
              display: showDeleteModal ? "block" : "none",
              backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "",
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content slot-overview-modal-content">
                <div className="modal-header slot-overview-modal-header">
                  <h5 className="modal-title d-flex align-items-center justify-content-center text-center gap-2">
                    <Icon
                      icon="mdi:alert-circle"
                      className="slot-overview-modal-icon-danger"
                    />
                    Confirm Deletion
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleDeleteCancel}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body slot-overview-modal-body">
                  <p className="slot-overview-modal-text">
                    Are you sure you want to delete{" "}
                    <strong className="slot-overview-modal-strong-danger">
                      Slot #{slotToDelete?.label || "this slot"}
                    </strong>
                    ?
                  </p>
                  <p className="slot-overview-modal-subtext">
                    This action cannot be undone and will permanently remove the
                    slot from the system.
                  </p>
                </div>
                <div className="modal-footer slot-overview-modal-footer">
                  
                  <button
                    type="button"
                    className="btn btn-secondary slot-overview-modal-cancel"
                    onClick={handleDeleteCancel}
                  >
                    Cancel
                  </button>
                  {hasPermission("delete_slot") && (
                  <button
                    type="button"
                    disabled={loading}
                    className="btn btn-danger slot-overview-modal-delete"
                    onClick={handleDeleteConfirm}
                  >
                    {loading ? "Please wait..." : "Delete Slot"}
                  </button>)}
                </div>
              </div>
            </div>
          </div>

          {/* Create Slot Modal */}
          <div
            className={`modal fade ${showCreateModal ? "show d-block" : ""}`}
            tabIndex="-1"
            role="dialog"
            style={{
              display: showCreateModal ? "block" : "none",
              backgroundColor: showCreateModal ? "rgba(0,0,0,0.5)" : "",
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content slot-overview-modal-content">
                <div className="modal-header slot-overview-modal-header">
                  <h5 className="modal-title">Create New Slots</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCreateCancel}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleCreateSubmit}>
                  <div className="modal-body slot-overview-modal-body">
                    <div className="mb-3 border p-3 rounded">
                      <div className="row">
                        <div className="col">
                          <strong>Your Plan:</strong>
                        </div>
                        <div className="col">
                          <p className="justify-content-end text-end">
                            {planLimits.planName} ({planLimits.maxSlots} total
                            slots)
                          </p>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <strong>Slots Created:</strong>
                        </div>
                        <div className="col">
                          <p className="justify-content-end text-end">
                            {planLimits.slotsCreated} / {planLimits.maxSlots}
                          </p>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <strong>Slots Remaining:</strong>
                        </div>
                        <div className="col">
                          <p className="justify-content-end text-end">
                            {planLimits.slotsLeft}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="slot-overview-form-group">
                      <label
                        htmlFor="slot_count"
                        className="slot-overview-label"
                      >
                        Number of Slots{" "}
                        <span className="slot-overview-required">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control radius-8 ${
                          formErrors.slot_count
                            ? "slot-overview-input-error"
                            : ""
                        }`}
                        id="slot_count"
                        name="slot_count"
                        value={formData.slot_count}
                        onChange={handleInputChange}
                        placeholder={`Enter number of slots (max ${planLimits.slotsLeft})`}
                        min="1"
                        max={planLimits.slotsLeft}
                        disabled={loading || planLimits.slotsLeft <= 0}
                      />
                      {formErrors.slot_count && (
                        <span className="slot-overview-error-message">
                          {formErrors.slot_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer slot-overview-modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCreateCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary-600"
                      disabled={loading || planLimits.slotsLeft <= 0}
                    >
                      {loading ? "Please wait..." : "Create Slots"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Edit Slot Modal */}
          <div
            className={`modal fade ${showEditModal ? "show d-block" : ""}`}
            tabIndex="-1"
            role="dialog"
            style={{
              display: showEditModal ? "block" : "none",
              backgroundColor: showEditModal ? "rgba(0,0,0,0.5)" : "",
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content slot-overview-modal-content">
                <div className="modal-header slot-overview-modal-header">
                  <h5 className="modal-title">
                    Edit Slot #{slotToEdit?.slot_number}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleEditCancel}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleEditSubmit}>
                  <div className="modal-body slot-overview-modal-body">
                    <div className="slot-overview-form-group">
                      <label
                        htmlFor="slotNumber"
                        className="slot-overview-label"
                      >
                        Slot Number{" "}
                        <span className="slot-overview-required">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control radius-8 ${
                          editFormErrors.slotNumber
                            ? "slot-overview-input-error"
                            : ""
                        }`}
                        id="slotNumber"
                        name="slotNumber"
                        value={editFormData.slotNumber}
                        onChange={handleEditTextInputChange}
                        placeholder="e.g., Slot A1"
                        readOnly
                      />
                      {editFormErrors.slotNumber && (
                        <span className="slot-overview-error-message">
                          {editFormErrors.slotNumber}
                        </span>
                      )}
                    </div>
                    <div className="slot-overview-form-group">
                      <label
                        htmlFor="slot_type"
                        className="slot-overview-label"
                      >
                        Slot Type{" "}
                        <span className="slot-overview-required">*</span>
                      </label>
                      <CustomSelect
                        name="slot_type"
                        value={editFormData.slot_type}
                        onChange={(selected) =>
                          handleEditInputChange(selected, "slot_type")
                        }
                        options={slotTypes}
                        placeholder="Select Slot Type"
                        labelKey="label"
                        valueKey="value"
                        isSearchable={true}
                        isClearable={true}
                        style={{
                          border: editFormErrors.slot_type
                            ? "1px solid #e53e3e"
                            : "",
                        }}
                      />
                      {editFormErrors.slot_type && (
                        <span className="slot-overview-error-message">
                          {editFormErrors.slot_type}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer slot-overview-modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary slot-overview-modal-cancel"
                      onClick={handleEditCancel}
                    >
                      Cancel
                    </button>
                    {hasPermission("edit_slot") && ( <button
                      type="submit"
                      className="btn btn-primary-600"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div
                            style={{
                              border: "2px solid rgba(255, 255, 255, 0.3)",
                              borderRadius: "50%",
                              borderTopColor: "#fff",
                              width: "16px",
                              height: "16px",
                              animation: "spin 1s ease-in-out infinite",
                              display: "inline-block",
                              marginRight: "8px",
                            }}
                          />
                          Please wait...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button> ) }
                   
                  </div>
                </form>
              </div>
            </div>
          </div>

          {(showDeleteModal || showCreateModal || showEditModal) && (
            <div className="no-modal fade show"></div>
          )}
        </div>
      </div>
    </>
  );
};

export default SlotOverviewPage;



const SlotOverviewShimmer = () => {
  return (
    <div className="slot-overview-card">
      {/* Stats Grid - 4 Cards */}
      <div className="row g-3 mb-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-5 d-flex align-items-center gap-3">
                {/* Icon Placeholder */}
                <div className="placeholder-glow">
                  <div
                    className="placeholder rounded-3"
                    style={{ width: "48px", height: "48px" }}
                  ></div>
                </div>

                {/* Text Placeholders */}
                <div className="flex-grow-1 placeholder-glow">
                  <div className="placeholder col-8 mb-2" style={{ height: "20px" }}></div>
                  <div className="placeholder col-10" style={{ height: "16px" }}></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Plan Usage Card */}
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 col-xl-6">
          <div className="card border-0 shadow-sm radius-12">
            <div className="card-body p-4 text-center">
              {/* Title + Badge */}
              <div className="placeholder-glow mb-4">
                <div className="mt-3"><span className="placeholder col-3 ms-2 rounded-pill" style={{ height: "28px" }}></span></div>
               <div className="mt-3"> <span className="placeholder col-5 d-inline-block" style={{ height: "28px" }}></span></div>
              </div>

              {/* Description */}
              {/* <div className="placeholder-glow mb-4">
                <div className="placeholder col-8 mx-auto" style={{ height: "20px" }}></div>
              </div> */}

              {/* Progress Info */}
              <div className="d-flex justify-content-between align-items-center mb-2 placeholder-glow mt-3">
                <span className="placeholder col-1" style={{ height: "16px" }}></span>
                <span className="placeholder col-1" style={{ height: "16px" }}></span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2 placeholder-glow mt-2">
                <span className="placeholder col-12" style={{ height: "16px" }}></span>
              </div>

              {/* Progress Bar */}
              {/* <div className="progress rounded-pill mb-3" style={{ height: "10px" }}>
                <div
                  className="progress-bar placeholder-glow"
                  role="progressbar"
                  style={{ width: "65%" }}
                >
                  <span className="placeholder w-100 h-100"></span>
                </div>
              </div> */}

              {/* Small Text Below Progress */}
              <div className="placeholder-glow text-end mb-4">
                <span className="placeholder col-1 d-inline-block" style={{ height: "14px" }}></span>
              </div>

              {/* Button */}
              <div className="placeholder-glow">
                <div
                  className="placeholder col-2 mx-auto rounded-3"
                  style={{ height: "48px" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

